import Box from '@mui/material/Box';
import { useTheme, type SxProps, type Theme } from '@mui/material/styles';

// The brand's own colours: the mark looks the same whatever theme preset is active.
const MARK = {
  light: { ring: '#16324F', north: '#E8873A', south: '#0F8B83', hub: '#FFFFFF' },
  dark: { ring: '#FFFFFF', north: '#E8873A', south: '#3CC2B7', hub: '#16324F' },
} as const;

/**
 * The Camptain mark (Compass C): the C as a compass ring, the needle pointing ahead and a
 * graduation tassel. `tone` is the background it sits on; by default the theme's own
 * (light mode → light, dark mode → dark). Give `title` when the mark stands alone; leave it
 * out when the name "Camptain" is next to it. Source of truth: shared/brand/camptain-mark.svg.
 */
export function CamptainMark({
  size,
  tone,
  title,
  sx,
}: {
  size: number | string;
  tone?: 'light' | 'dark';
  title?: string;
  sx?: SxProps<Theme>;
}) {
  const theme = useTheme();
  const c = MARK[tone ?? theme.palette.mode];
  return (
    <Box
      component="svg"
      viewBox="63 64 368 384"
      role={title ? 'img' : undefined}
      aria-hidden={title ? undefined : true}
      sx={
        [
          { width: size, height: size, flexShrink: 0, display: 'block' },
          ...(Array.isArray(sx) ? (sx as readonly SxProps<Theme>[]) : [sx]),
        ] as SxProps<Theme>
      }
    >
      {title && <title>{title}</title>}
      <path
        d="M378.6 153.2A160 160 0 1 0 378.6 358.8"
        fill="none"
        stroke={c.ring}
        strokeWidth={48}
        strokeLinecap="round"
      />
      <g stroke={c.ring} strokeWidth={12} strokeLinecap="round">
        <line x1={256} y1={132} x2={256} y2={150} />
        <line x1={132} y1={256} x2={150} y2={256} />
        <line x1={256} y1={362} x2={256} y2={380} />
      </g>
      <polygon points="352,160 232.4,232.4 279.6,279.6" fill={c.north} />
      <polygon points="160,352 232.4,232.4 279.6,279.6" fill={c.south} />
      <circle cx={256} cy={256} r={13} fill={c.hub} />
      <path
        d="M378.6 153.2Q404 176 404 214"
        fill="none"
        stroke={c.north}
        strokeWidth={9}
        strokeLinecap="round"
      />
      <path d="M392 208h24l6 52q-18 10-36 0Z" fill={c.north} />
    </Box>
  );
}
